import { useSubmit } from "@certn/api";
import { Card, Icon, showToast, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ArchivePaymentProfileModal } from "./ArchivePaymentProfileModal";
import { PaymentProfileActionDropdown } from "./PaymentProfileActionDropdown";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { doesCardHaveIcon, getPaymentIcon, isCardExpired } from "../helpers";

interface PaymentCardProps {
  brand: string;
  number: string;
  expiryDate: string;
  owner: string;
  issuer: string;
  title: string;
  id: string;
  isArchived: boolean;
  mutate: () => void;
}

export function PaymentCard(props: PaymentCardProps) {
  const { brand, number, expiryDate, owner, title, id, isArchived, mutate } =
    props;
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const [showArchivePaymentProfileModal, setShowArchivePaymentProfileModal] =
    useState(false);
  const formattedExpiryDate = `${expiryDate.split("-")[1]}/${expiryDate.split("-")[0]}`;

  const { submit: archiveProfile } = useSubmit(
    `/api/internal/client-portal/payment-profiles/${id}/archive/`,
    {
      onSuccess: async () => {
        await mutate();
        setShowArchivePaymentProfileModal(false);
        showToast(t("Payment profile archived"), { type: "success" });
      },
    }
  );

  const { submit: restoreProfile } = useSubmit(
    `/api/internal/client-portal/payment-profiles/${id}/restore/`,
    {
      onSuccess: async () => {
        await mutate();
        setShowArchivePaymentProfileModal(false);
        showToast(t("Payment profile restored"), { type: "success" });
      },
    }
  );

  return (
    <Card key={id} border="border-base" className={styles.card}>
      <div className={styles.leftGroup}>
        <div className={styles.icon}>
          {doesCardHaveIcon(brand) ? (
            <img
              src={getPaymentIcon(brand)}
              alt={brand}
              width="24px"
              height="24px"
            />
          ) : (
            <Icon name="credit_card" size="space-6" />
          )}
        </div>
        <div className={styles.info}>
          {title && number && (
            <Text style="global/body-primary">
              {title} ****
              {number}
            </Text>
          )}
          {brand && <Text style="global/body-secondary">{brand}</Text>}
          {expiryDate && (
            <div className={styles.bottomRow}>
              {isCardExpired(formattedExpiryDate) ? (
                <Text
                  style="global/body-secondary"
                  color="background-accent-red-strong"
                >
                  {t("Expired")} {formattedExpiryDate}
                </Text>
              ) : (
                <Text style="global/body-secondary">
                  {t("Expires")} {formattedExpiryDate}{" "}
                </Text>
              )}
              {owner && (
                <span className={styles.spacer}>
                  <Text style="global/body-secondary">
                    {" \u00B7 "} {owner}
                  </Text>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.actionDropdown}>
        <PaymentProfileActionDropdown
          profileId={id}
          isArchived={isArchived}
          handleOpenArchiveModal={() => setShowArchivePaymentProfileModal(true)}
          handleRestore={() => restoreProfile()}
        />
      </div>
      {showArchivePaymentProfileModal && (
        <ArchivePaymentProfileModal
          title={title}
          clientId={client?.id}
          paymentProfileId={id}
          handleArchive={() => archiveProfile()}
          handleClose={() => setShowArchivePaymentProfileModal(false)}
        />
      )}
    </Card>
  );
}

const styles = {
  leftGroup: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
  }),
  actionDropdown: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "end",
    margin: "auto 0",
  }),
  spacer: css({
    paddingLeft: "var(--space-1)",
  }),
  bottomRow: css({
    display: "flex",
    flexDirection: "row",
  }),
  card: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "var(--space-4)",
  }),
  icon: css({
    marginRight: "var(--space-3)",
  }),
  info: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    alignItems: "left",
  }),
};
